import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { updateTeam } from "../../../redux/Slice/Settings/settingsSlice";
import { toast } from "react-hot-toast";
import { isSubscriptionBlockedError, checkSubscriptionActive } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";

const EditTeam = ({ open, onClose, team }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.settings);
  
  const [formData, setFormData] = useState({
    teamName: "",
    description: "",
  });
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (team) {
      setFormData({
        teamName: team.teamName || "",
        description: team.description || "",
      });
    }
  }, [team]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear local error when user starts typing
    if (localError) setLocalError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.teamName.trim()) {
      setLocalError("Team name is required");
      return;
    }

    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }

    try {
      await dispatch(updateTeam({
        teamId: team._id,
        teamData: formData
      })).unwrap();

      onClose();
      setLocalError("");
      toast.success("Team updated successfully.");
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        setLocalError(typeof error === 'string' ? error : "Failed to update team.");
      }
    }
  };

  const handleClose = () => {
    setFormData({
      teamName: team?.teamName || "",
      description: team?.description || "",
    });
    setLocalError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "background.paper",
          color: "text.primary",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Edit Team
        </Typography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {(error || localError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {localError || error}
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Team Name"
            name="teamName"
            value={formData.teamName}
            onChange={handleInputChange}
            margin="normal"
            required
            sx={{
              "& .MuiInputLabel-root": {
                color: "text.secondary",
              },
              "& .MuiInputBase-input": {
                color: "text.primary",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.23)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
          />
          
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={3}
            sx={{
              "& .MuiInputLabel-root": {
                color: "text.secondary",
              },
              "& .MuiInputBase-input": {
                color: "text.primary",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.23)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(255, 255, 255, 0.12)",
                color: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            {loading ? "Updating..." : "Update Team"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditTeam; 